import { randomInt } from '../../lib/random';
import { MAX_KIKOO_LENGTH } from '../../lib/sanitize';

/**
 * Le générateur de pseudo kikoo.
 *
 * C'est le rituel de l'époque : on n'arrivait pas sur un Skyblog avec son
 * prénom, on arrivait avec `xX_k3ViN_du_57_Xx`. Le générateur reproduit la
 * fabrique, étage par étage, et **le pseudo obtenu suit le visiteur sur les
 * onze interfaces** : c'est lui qui signe les messages du livre d'or, dans le
 * bac d'AquariumOS comme dans la page de discussion de Poissonpédia.
 *
 * Tout est pur ici — pas de DOM, pas d'état, pas de hasard sauf quand on le
 * demande explicitement. Chaque interrupteur est une transformation vérifiable
 * isolément, et l'ordre dans lequel ils s'appliquent est le seul point délicat :
 * il est fixé une fois pour toutes dans `kikooName`.
 */

/**
 * Longueur maximale du blaze de départ, avant décoration.
 *
 * Elle est choisie pour que le pire cas du générateur reste sous
 * `MAX_KIKOO_LENGTH` : un test unitaire le vérifie plutôt que de le supposer.
 */
export const MAX_KIKOO_BASE = 14;

/** Les six interrupteurs, dans l'ordre où la boîte les présente. */
export const KIKOO_TOGGLES = ['leet', 'alternate', 'dept', 'mirror', 'wrap', 'glitter'] as const;
export type KikooToggle = (typeof KIKOO_TOGGLES)[number];

export type KikooOptions = Readonly<Record<KikooToggle, boolean>>;

/**
 * Réglage de départ : les trois transformations que **tout le monde** faisait.
 * Les trois autres sont là pour ceux qui poussaient plus loin.
 */
export const DEFAULT_KIKOO_OPTIONS: KikooOptions = {
  leet: true,
  alternate: true,
  dept: false,
  mirror: false,
  wrap: true,
  glitter: false,
};

/** Blazes proposés par le bouton « au hasard ». */
export const KIKOO_SEEDS: readonly string[] = [
  'kevin',
  'steve',
  'lolita',
  'jenyfer',
  'dylan',
  'mickael',
  'sandra',
  'bryan',
  'cindy',
  'jordan',
];

/** Départements que le générateur sait attribuer. */
export const KIKOO_DEPTS: readonly number[] = [6, 13, 44, 57, 59, 62, 69, 75, 92, 93];

/** Substitutions chiffrées, dans leur version la plus lisible. */
const LEET: Readonly<Record<string, string>> = {
  a: '4',
  e: '3',
  i: '1',
  o: '0',
  s: '5',
};

/** Les deux bouts de guirlande, mis en miroir l'un de l'autre. */
const GLITTER_OPEN = '★彡';
const GLITTER_CLOSE = '彡★';

/**
 * Ramène une saisie libre à un blaze utilisable.
 *
 * Les accents sont dépliés puis retirés — un pseudo de 2005 ne portait pas de
 * cédille. Les espaces deviennent des tirets bas, tout le reste disparaît, et
 * ce qui sort est en minuscules : c'est la casse alternée qui décide ensuite.
 */
export function normaliseBase(raw: string): string {
  if (typeof raw !== 'string') return '';
  return raw
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/gu, '_')
    .replace(/[^a-z0-9_-]/gu, '')
    .replace(/_{2,}/gu, '_')
    .replace(/^[_-]+|[_-]+$/gu, '')
    .slice(0, MAX_KIKOO_BASE);
}

/**
 * Le département d'un blaze.
 *
 * Il n'est pas demandé au visiteur, il est **déduit du pseudo** : le même blaze
 * donne toujours le même département, ce qui est faux, arbitraire, et exactement
 * aussi sérieux que le reste de la boîte.
 */
export function deptFor(base: string): number {
  let hash = 0;
  for (const char of base) {
    hash = (hash * 31 + (char.codePointAt(0) ?? 0)) % 100_000;
  }
  return KIKOO_DEPTS[hash % KIKOO_DEPTS.length] ?? 57;
}

/** « 6 » s'écrit « 06 » : un département tient sur deux chiffres. */
export function formatDept(dept: number): string {
  return String(dept).padStart(2, '0');
}

/** Remplace les lettres qui ont un chiffre pour sosie. */
function leetify(input: string): string {
  return [...input].map((char) => LEET[char] ?? char).join('');
}

/**
 * Casse alternée, **comptée sur les lettres seules**.
 *
 * Sans ça, un tiret bas ou un chiffre consommerait un tour et la lecture
 * s'effondrerait : `k3v1n` deviendrait `K3v1N` au lieu de `K3V1N`.
 */
function alternateCase(input: string): string {
  let letters = 0;
  let out = '';
  for (const char of input) {
    const lower = char.toLowerCase();
    const upper = char.toUpperCase();
    if (lower === upper) {
      out += char;
      continue;
    }
    out += letters % 2 === 0 ? lower : upper;
    letters += 1;
  }
  return out;
}

/**
 * Le pseudo fini.
 *
 * L'ordre des étages n'est pas négociable : le reflet double le blaze **avant**
 * que les chiffres et la casse ne s'appliquent (sinon les deux moitiés ne se
 * ressemblent plus), le département arrive avant l'emballage (il est dans le
 * pseudo, pas autour), et la guirlande est toujours la couche extérieure.
 *
 * Renvoie une chaîne vide si le blaze ne contenait rien d'utilisable : la boîte
 * n'a alors rien à proposer, et le bouton d'adoption reste inerte.
 */
export function kikooName(raw: string, options: KikooOptions): string {
  const base = normaliseBase(raw);
  if (base === '') return '';

  let name = base;
  if (options.mirror) name = `${name}_${[...base].reverse().join('')}`;
  if (options.leet) name = leetify(name);
  if (options.alternate) name = alternateCase(name);
  if (options.dept) name = `${name}_du_${formatDept(deptFor(base))}`;
  if (options.wrap) name = `xX_${name}_Xx`;
  if (options.glitter) name = `${GLITTER_OPEN}${name}${GLITTER_CLOSE}`;

  return name.slice(0, MAX_KIKOO_LENGTH);
}

/** Un blaze et des interrupteurs tirés au sort, pour le bouton « au hasard ». */
export function randomKikoo(pick: (max: number) => number = randomInt): {
  readonly base: string;
  readonly options: KikooOptions;
} {
  const base = KIKOO_SEEDS[pick(KIKOO_SEEDS.length)] ?? 'kevin';
  const drawn: Record<KikooToggle, boolean> = { ...DEFAULT_KIKOO_OPTIONS };
  for (const toggle of KIKOO_TOGGLES) drawn[toggle] = pick(2) === 1;

  // Un tirage tout éteint rendrait le prénom tel quel : ce n'est pas un pseudo,
  // c'est une saisie. On rallume alors les deux transformations de base.
  const lit = KIKOO_TOGGLES.some((toggle) => drawn[toggle]);
  return { base, options: lit ? drawn : { ...drawn, leet: true, wrap: true } };
}
